import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, Wrench, Headphones, CheckCircle, Star } from 'lucide-react';
import { useLanguage } from '../LanguageContext';

const Services = () => {
  const { t } = useLanguage();
  
  // Services with translation keys
  const services = [
    {
      id: 1,
      nameKey: 'standardInstallation',
      descriptionKey: 'standardInstallationDesc',
      price: 99.99,
      duration: t('duration23hours'),
      featuresKeys: ['deviceInstallation', 'networkConfig', 'basicTraining', 'support30days'],
      popular: false
    },
    {
      id: 2,
      nameKey: 'premiumInstallation',
      descriptionKey: 'premiumInstallationDesc',
      price: 199.99,
      duration: t('duration34hours'),
      featuresKeys: ['allStandard', 'advancedConfig', 'customScenarios', 'completeTraining', 'support90days'],
      popular: true
    },
    {
      id: 3,
      nameKey: 'remoteConfiguration',
      descriptionKey: 'remoteConfigurationDesc',
      price: 49.99,
      duration: t('duration1hour'),
      featuresKeys: ['liveVideoSupport', 'softwareConfig', 'personalizedGuide', 'support14days'],
      popular: false
    }
  ];
  
  const scrollToReservation = () => {
    const element = document.getElementById('reservation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-blue-100 text-blue-700 mb-4">{t('ourServices')}</Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('servicesTitle')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('servicesSubtitle')}
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-16 rounded-3xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <div className="relative h-96">
            <img
              src="https://images.unsplash.com/photo-1687819280272-95f9d2a3cdab"
              alt={t('professionalInstallation')}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/80 to-blue-400/60 flex items-center justify-center">
              <div className="text-center text-white px-4">
                <Wrench className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-3xl font-bold mb-2">{t('installationIn')}</h3>
                <p className="text-lg">{t('professionalServiceGuarantee')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <Card
              key={service.id}
              className={`hover:shadow-2xl transition-all duration-300 relative ${
                service.popular ? 'border-2 border-blue-600 shadow-xl' : 'border-2 hover:border-blue-200'
              }`}
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`
              }}
            >
              {service.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <Badge className="bg-blue-600 text-white px-4 py-1 text-sm shadow-lg">
                    <Star className="w-3 h-3 mr-1 inline" fill="currentColor" />
                    {t('mostPopular')}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl text-gray-900 mb-2">{t(service.nameKey)}</CardTitle>
                <CardDescription className="text-gray-600">{t(service.descriptionKey)}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="text-center">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-blue-600">${service.price}</span>
                    <span className="text-gray-500 ml-2">CAD</span>
                  </div>
                  <div className="flex items-center justify-center mt-2 text-gray-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span className="text-sm">{service.duration}</span>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  {service.featuresKeys.map((featureKey, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{t(featureKey)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  onClick={scrollToReservation}
                  className={`w-full ${
                    service.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {t('reserveService')}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Why Choose Us */}
        <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 max-w-5xl mx-auto">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('whyChooseUs')}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">{t('localExpertise')}</h4>
              <p className="text-gray-600">{t('localExpertiseDesc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">{t('quickInstallation')}</h4>
              <p className="text-gray-600">{t('quickInstallationDesc')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">{t('continuousSupport')}</h4>
              <p className="text-gray-600">{t('continuousSupportDesc')}</p>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default Services;
